import {
  Content,
  Arrow as RadixTooltipArrow,
  Root as Tooltip,
  Portal as TooltipPortal,
  Provider as TooltipProvider,
  Trigger as TooltipTrigger,
} from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { tooltipArrowClasses, tooltipContentClasses } from './tooltip.styles';
import type {
  TooltipArrowProps,
  TooltipCollisionPadding,
  TooltipContentProps,
} from './tooltip.types';

// In the desktop app a fixed title bar overlays the top of the webview and
// paints above tooltips (`TauriAppHeader`, `z-[99999]`; on macOS the native
// traffic-light strip occupies the same 32px). Radix's collision detection
// uses the raw webview viewport, so without an offset top-side tooltips render
// into that strip and get hidden behind it. The bar height arrives as the
// `--app-titlebar-height` var on `<html>` (published by the app's
// `TauriTitlebarHeightVar`, absent — i.e. 0 — on web and in auxiliary Tauri
// windows), keeping this package platform-agnostic: inject it as a top
// `collisionPadding` so Radix flips the tooltip below (or shifts it down for
// left/right placement) when a top-side render would land inside the strip.
const getTitlebarInset = (): number => {
  if (typeof document === 'undefined') return 0;
  // Absent during SSR and while a document is being torn down or replaced —
  // no root element means no published var, which is the same as no titlebar.
  const root = document.documentElement;
  if (!root) return 0;
  // Inline-style read on purpose: `TauriTitlebarHeightVar` publishes the var
  // as an inline style on <html>, and reading `el.style` — unlike
  // `getComputedStyle`, which forces a synchronous style/layout flush — is a
  // plain attribute read. This runs on every TooltipContent render (open +
  // reposition), so it must stay flush-free.
  return Number.parseFloat(root.style.getPropertyValue('--app-titlebar-height')) || 0;
};

const withTitlebarInset = (
  callerValue: TooltipCollisionPadding
): TooltipCollisionPadding => {
  const topInset = getTitlebarInset();
  if (topInset === 0) return callerValue;
  if (callerValue == null) return { top: topInset };
  if (typeof callerValue === 'number') {
    return {
      top: Math.max(callerValue, topInset),
      right: callerValue,
      bottom: callerValue,
      left: callerValue,
    };
  }
  return {
    ...callerValue,
    top: Math.max(callerValue.top ?? 0, topInset),
  };
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, collisionPadding, ...props }, ref) => (
  <TooltipPortal>
    <Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={withTitlebarInset(collisionPadding)}
      className={cn(tooltipContentClasses, className)}
      {...props}
    />
  </TooltipPortal>
));
TooltipContent.displayName = Content.displayName;

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof RadixTooltipArrow>,
  TooltipArrowProps
>(({ className, ...props }, ref) => (
  <RadixTooltipArrow
    ref={ref}
    className={cn(tooltipArrowClasses, className)}
    {...props}
  />
));
TooltipArrow.displayName = RadixTooltipArrow.displayName;

export { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger };
